/**
 * Sentry共通設定
 *
 * クライアント、サーバー、エッジランタイム間で共有されるSentry設定
 */

const ENVIRONMENT = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'development';

/**
 * 環境別の trace サンプリングレート。
 *
 * 本番は分散トレーシングのコスト/ノイズ対策で間引き、stg/dev は計測しやすいよう高めに保つ。
 * （エラーキャプチャ自体はサンプリングの影響を受けない。これはあくまで performance trace の割合）
 */
function resolveTracesSampleRate(environment: string): number {
  if (environment === 'production') return 0.3;
  return 1.0;
}

/**
 * 分散トレーシングの伝播先。
 *
 * ここにマッチする URL への fetch/XHR に `sentry-trace`/`baggage` ヘッダが自動付与され、
 * BE 側の trace と1本に繋がる。GraphQL API は別オリジン（NEXT_PUBLIC_API_ENDPOINT）のため
 * 明示的に許可しないとヘッダが付かず FE↔BE が断絶する。相対パス（/^\//）も含める。
 */
function resolveTracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = [/^\//];
  const apiEndpoint = process.env.NEXT_PUBLIC_API_ENDPOINT;
  if (apiEndpoint) {
    targets.push(apiEndpoint);
  }
  return targets;
}

export const SENTRY_CONFIG = {
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: ENVIRONMENT,
  // DSNが設定されていれば有効化
  enabled: Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN),

  // パフォーマンストレースのサンプリングレート（環境別）
  tracesSampleRate: resolveTracesSampleRate(ENVIRONMENT),

  // 分散トレーシングの伝播先（FE→BE→DB を1トレースに繋ぐ）
  tracePropagationTargets: resolveTracePropagationTargets(),

  // 通常セッションの10%を録画
  replaysSessionSampleRate: 0.1,

  // エラー発生時のセッションは100%録画
  replaysOnErrorSampleRate: 1.0,
} as const;

/**
 * Sentryが有効かどうかをチェック
 */
export const isSentryEnabled = () => SENTRY_CONFIG.enabled && Boolean(SENTRY_CONFIG.dsn);
